export const en = {
  breadcrumb: "Settings",
  storeConnection: {
    metaTitle: "Store Connection - OrderHub",
    metaDescription: "Connect and manage your Shopify store integration.",
    title: "Store Connection",
    breadcrumb: "Store Connection",
    subtitleDisconnected: "Link your Shopify store to start syncing orders and customers",
    subtitleConnected: "Your Shopify store is connected and syncing",
    actionsTitle: "Actions",
    actionsDescription: "Manage your store connection",
    connectSuccessToast: "Store connected successfully.",
    connectFailedToast: "Connection not completed. Please try again.",
    reconnectFailedToast: "Failed to start reconnect. Please try again.",
    resyncFailedToast: "Failed to start resync. Please try again.",
    errors: {
      storeAlreadyConnected:
        "This Shopify store is already connected to another workspace.",
    },
    connecting: {
      heading: "Connecting to your store…",
      description: "You'll be redirected to Shopify to authorize the connection. This page updates automatically when complete.",
    },
    card: {
      enterDomainTitle: "Enter Your Store Domain",
      noStoreTitle: "No Store Connected",
      enterDomainDescription: "We'll redirect you to Shopify to authorize the connection",
      noStoreDescription: "Connect your Shopify store to unlock all features",
      readyToConnect: "Ready to connect",
      readyToConnectDescription: "Securely link your Shopify store via official OAuth",
    },
    badge: {
      connected: "Connected",
      disconnected: "Disconnected",
    },
    input: {
      domainAriaLabel: "Shopify store domain",
      domainPlaceholder: "your-store",
      domainError: "Enter a valid store name (letters, numbers, hyphens)",
    },
    actions: {
      connectShopifyStore: "Connect Shopify Store",
      connect: "Connect",
      connecting: "Connecting…",
      runResync: "Run Resync Now",
      resyncStarting: "Starting...",
      reconnectShopify: "Reconnect Shopify",
      reconnecting: "Reconnecting...",
      disconnectConfirm: "Disconnect this store? Data will stop syncing.",
      disconnectStore: "Disconnect Store",
    },
    disconnectDialog: {
      subscriptionActiveTitle: "Your subscription is still active",
      subscriptionActiveSubtitle: "Disconnecting won't cancel your billing",
      subscriptionActiveBody: "Disconnecting this store won't cancel your subscription — you'll keep being billed via Shopify. To stop billing, cancel your subscription from the Billing page first.",
      youWillKeepBeingBilled: "You'll keep being billed",
      billingContinues: "Billing continues via Shopify",
      dataSyncStops: "Data sync stops immediately",
      reconnectAnytime: "Reconnect anytime to resume",
      cancelSubscriptionFirst: "Cancel subscription first",
      disconnectAnyway: "Disconnect anyway",
      simpleTitle: "Disconnect store?",
      simpleSubtitle: "This will stop data sync from your store",
      simpleBody: "Data will stop syncing from this store. You can reconnect at any time to resume.",
      disconnect: "Disconnect",
      confirm: "Disconnect",
      cancel: "Cancel",
    },
    verifyEmailAlert: "Verify your email to connect a Shopify store.",
    details: {
      title: "Store Details",
      description: "Your connected Shopify store information",
      storeNameLabel: "Store Name",
      storeUrlLabel: "Store URL",
      connectedOnLabel: "Connected On",
      connectedByLabel: "Connected By",
      apiVersionLabel: "API Version",
      lastSyncLabel: "Last Sync",
    },
    scopes: {
      title: "Access Scopes",
      description: "Permissions granted by Shopify",
      countSuffix: "scopes",
    },
    webhook: {
      title: "Webhook Status",
      description: "Real-time event delivery from Shopify",
      inactiveFallback: "inactive",
      registeredPrefix: "Registered ",
      notRegistered: "Not registered",
    },
    reconnectAlert: {
      title: "Reconnect Shopify if you change store permissions or encounter connection issues.",
      description: "This will refresh authorization, validate scopes, and re-register webhooks.",
    },
  },
  triggerRules: {
    metaTitle: "Rules - OrderHub",
    metaDescription: "Configure which follow-up triggers create activities for your team.",
    title: "Rules",
    breadcrumb: "Rules",
    subtitle: "Configure which follow-up triggers create activities for your team",
    changesApplyAlert: "Trigger changes apply to new and newly processed records going forward.",
    historyLink: "History",
    verifyEmailAlert: "Verify your email to create or edit rules.",
    card: {
      recentCreatedLabel: "Recent Created",
      lastSevenDays: "Last 7 days",
      editDisabledTooltip: "Verify your email to edit triggers",
    },
    conditions: {
      paymentMethodsLabel: "Payment Methods",
      paymentMethodsDescription: "Only trigger for orders using these payment methods",
      paymentMethodsPlaceholder: "e.g. cod, card",
      minOrderValueLabel: "Minimum Order Value",
      minOrderValueDescription: "Skip orders below this value",
      includeTagsLabel: "Include Tags",
      includeTagsDescription: "Order must have at least one of these tags",
      includeTagsPlaceholder: "e.g. wholesale",
      excludeTagsLabel: "Exclude Tags",
      excludeTagsDescription: "Skip orders with any of these tags",
      excludeTagsPlaceholder: "e.g. confirmed",
      orderSourceLabel: "Order Source",
      orderSourceDescription: "Filter by the channel where the order originated",
      orderSourcePlaceholder: "e.g. web, mobile",
      minDraftValueLabel: "Minimum Draft Value",
      minDraftValueDescription: "Skip draft orders below this value",
      includeTagsDraftDescription: "Draft order must have at least one of these tags",
      excludeTagsDraftDescription: "Skip draft orders with any of these tags",
      requirePhoneLabel: "Require Phone",
      requirePhoneDescription: "Only include draft orders with a customer phone number on file",
      minCheckoutValueLabel: "Minimum Checkout Value",
      minCheckoutValueDescription: "Skip abandoned checkouts below this value",
      checkoutStepReachedLabel: "Checkout Step Reached",
      checkoutStepReachedDescription: "Only trigger for checkouts that reached at least one of these steps",
      checkoutStepReachedPlaceholder: "e.g. contact, shipping, payment",
      returningCustomerOnlyLabel: "Returning Customer Only",
      returningCustomerOnlyDescription: "Only trigger for customers with a prior order history",
      customerEnteredPhoneLabel: "Customer Entered Phone",
      customerEnteredPhoneDescription: "Only trigger when a phone number was captured during checkout",
      checkoutSourceLabel: "Checkout Source",
      checkoutSourceDescription: "Filter by the channel where the checkout originated",
      checkoutSourcePlaceholder: "e.g. web, mobile",
      includeCustomerTagsLabel: "Include Customer Tags",
      includeCustomerTagsDescription: "Customer must have at least one of these tags",
      includeCustomerTagsPlaceholder: "e.g. vip",
      excludeCustomerTagsLabel: "Exclude Customer Tags",
      excludeCustomerTagsDescription: "Skip checkouts from customers with any of these tags",
      excludeCustomerTagsPlaceholder: "e.g. do-not-contact",
      minValueRangeError: "Value must be a finite number between {{min}} and {{max}}.",
    },
    dialog: {
      editTitlePrefix: "Edit: ",
      loadingRule: "Loading rule...",
      enabledDescription: "Activate this trigger to start processing matching records",
      conditionsHeading: "Conditions",
      assignmentHeading: "Assignment",
      priorityLabel: "Priority",
      assignToLabel: "Assign to",
      selectTeamFirstHint: "Select a team first",
      unassignedOption: "Unassigned",
      syncToShopifyLabel: "Sync tag to shopify",
      syncTagPlaceholder: "e.g. cod-confirm",
      syncTagHelperText: "Add the following tag to matching shopify order.",
      changesApplyNote: "Changes apply to new matching records going forward.",
      updatedTriggerToast: "Changes has been saved",
    },
  },
  triggerRulesHistory: {
    title: "Rule Activity",
    activityLogBadge: "Activity Log",
    subtitle: "Track every change made to your order rules",
    stats: {
      totalActivity: "Total Activity",
      lastUpdated: "Last Updated",
      activeRules: "Active Rules",
      activeRulesSub: "of {{total}} total",
    },
    filterBy: "Filter by",
    clearFilters: "Clear filters",
    loadMore: "Load More Activity",
  },
  usersTeams: {
    metaTitle: "Users & Teams - OrderHub",
    metaDescription: "Manage users, their roles, and team assignments.",
    title: "Users & Teams",
    breadcrumb: "Users & Teams",
    subtitle: "Manage users, their roles, and team assignments.",
    rolesHeading: "Roles and permissions",
    roles: {
      repName: "Rep",
      repDescription: "Can manage assigned items and customers.",
      managerName: "Manager",
      managerDescription: "Can view team activity and reports.",
      adminName: "Admin",
      adminDescription: "Full access to settings, users, teams, and system configuration.",
      noteLabel: "Note",
      noteText: "Each user belongs to one team only",
    },
    usersCountHeading: "Users ({{count}})",
    inviteUser: "Invite User",
    inviteUserLockedTooltip: "Upgrade your plan to invite users",
    noTeam: "No team",
    table: {
      userHeader: "User",
      emailHeader: "Email",
      roleHeader: "Role",
      teamHeader: "Team",
      statusHeader: "Status",
      actionsHeader: "Actions",
      editUserAriaLabel: "Edit user",
      editUserTooltip: "Edit user",
      removeUserAriaLabel: "Remove user",
      removeUserTooltip: "Remove user",
      cancelInvitationAriaLabel: "Cancel invitation",
      cancelInvitationTooltip: "Cancel invitation",
    },
    teamsHeading: "Teams",
    addTeam: "Add Team",
    memberCount: "{{count}} members",
    editTeamAriaLabel: "Edit team",
    growthPlanSuffix: " (Growth)",
    inviteDialog: {
      title: "Invite User",
      sentToastTitle: "Invitation sent",
      sentToastDescription: "Email sent to {{email}}",
      failedToastTitle: "Failed to send invitation",
      inviteButton: "Invite",
    },
    teamDialog: {
      editTitle: "Edit Team",
      addTitle: "Add Team",
      teamNameLabel: "Team Name",
      updatedToastTitle: "Team updated",
      updatedToastDescription: "\"{{name}}\" has been saved",
      updateFailedToastTitle: "Failed to update team",
      createdToastTitle: "Team created",
      createdToastDescription: "\"{{name}}\" has been added",
      createFailedToastTitle: "Failed to create team",
    },
    editUserDialog: {
      title: "Edit User",
      updatedToastTitle: "User updated",
      updatedToastDescription: "{{name}}'s settings have been updated",
      updateFailedToastTitle: "Failed to update user",
    },
    resendButton: {
      resentToastTitle: "Invitation resent",
      resentToastDescription: "Email sent to {{email}}",
      failedToastTitle: "Failed to resend invitation",
      ariaLabel: "Resend invitation",
      tooltip: "Resend invitation",
    },
    cancelDialog: {
      title: "Cancel Invitation",
      body: "This will cancel the pending invitation for {{email}}. They will no longer be able to join the workspace using this invitation.",
      keepInvitation: "Keep Invitation",
      cancelledToastTitle: "Invitation cancelled",
      cancelledToastDescription: "{{email}} will no longer be able to join",
      cancelFailedToastTitle: "Failed to cancel invitation",
      cancelling: "Cancelling...",
      cancelInvitationButton: "Cancel Invitation",
    },
    delete: {
      successTitle: "User deleted",
      successDescription: "{{name}} has been removed from the workspace",
      title: "Delete User",
      subtitle: "This permanently deletes the user and frees their email for re-invite.",
      whatHappensTitle: "What happens next",
      bulletSignin: "They're signed out immediately and lose all access.",
      bulletActivities: "Open & snoozed activities move to the selected teammate.",
      bulletHistory: "Past activity keeps showing {{name}}'s name for ~6 months, then shows \"Previous user\".",
      bulletEmail: "Their email is freed and can be invited again anytime.",
      transferLabel: "Transfer active activities to",
      change: "Change",
      searchPlaceholder: "Search users...",
      noUsersFound: "No users found",
      confirmText: "I understand this action cannot be undone and the user will be permanently removed from the workspace.",
      deleting: "Deleting...",
      confirmButton: "Delete User",
    },
  },
  syncHealth: {
    metaTitle: "Sync Health - OrderHub",
    metaDescription: "Real-time health of data sync and background jobs.",
    title: "Sync Health",
    breadcrumb: "Sync Health",
    subtitle: "Real-time health of data sync and background jobs.",
    loadError: "Failed to load sync health.",
    status: {
      healthy: "Healthy",
      running: "Running",
      errored: "Errored",
      unknown: "Unknown",
    },
    connectionStatusLabel: "Connection status: ",
    connectionConnected: "Connected and working",
    connectionNotConnected: "Not connected",
    pendingUpdateAlert: {
      title: "Pending Update",
      description: "Sync has not run yet. This is normal for a new installation. Data will appear here once the first sync completes.",
    },
    pipeline: {
      unknownName: "Unknown",
      lastSuccessfulPrefix: "Last successful: ",
      orders: "Orders (Shopify → OrderHub)",
      draft_orders: "Draft Orders (Shopify → OrderHub)",
      checkouts: "Checkouts (Shopify → OrderHub)",
      customers: "Customers (Shopify → OrderHub)",
      reconciliation: "30-min Reconciliation",
    },
    healthyAlert: {
      title: "All systems are healthy",
      description: "Data is syncing as expected.",
    },
    runningAlert: {
      title: "Sync in progress",
      description: "Pipelines are currently running. Check back shortly for updated status.",
    },
    erroredAlert: {
      title: "Sync issues detected",
      description: "Some pipelines are not syncing correctly. Check the pipeline statuses above for details.",
    },
  },
} as const;
